import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ShiftSettings } from '@/components/staffAttendance/ShiftSettings';
import { WorkLocations } from '@/components/staffAttendance/WorkLocations';
import { FixedPaySettings } from '@/components/staffAttendance/FixedPaySettings';
import { SpecialDays } from '@/components/staffAttendance/SpecialDays';
import { FoodAllowanceSettings } from '@/components/staffAttendance/FoodAllowanceSettings';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';

const ACCESS = { hr: ['HR — all sections', 'bg-slate-800 text-white'], approve: ['Approves', 'bg-amber-100 text-amber-800'], reports: ['Reports only', 'bg-sky-100 text-sky-800'] };

/** Read-only: who has Staff Attendance access (set in User Management). */
const Approvers = ({ rows }) => (
  <Card className="border-0 shadow-md" data-testid="settings-approvers"><CardContent className="p-5">
    <h3 className="font-semibold">Who approves which section</h3>
    <p className="text-sm text-gray-500 mb-2">Set in <b>User Management</b> → Staff Attendance ticks (Approve Section Punches / Reports / HR Settings) + the user's <b>Sections</b>, or the <b>HR Manager</b> role. Admins always have full access.</p>
    {rows.length === 0 && <p className="text-sm text-gray-400" data-testid="settings-no-supervisors">No users have Staff Attendance access yet.</p>}
    <div className="max-h-96 overflow-y-auto pr-1">{rows.map((x) => (
      <div key={x.user_id} className="flex flex-wrap items-center gap-2 py-2 border-b last:border-0" data-testid={`sup-row-${x.user_id}`}>
        <div className="w-48 min-w-0"><div className="font-medium truncate">{x.name}</div><div className="text-[11px] text-gray-500">{x.role}</div></div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ACCESS[x.access][1]}`} data-testid={`sup-access-${x.user_id}`}>{ACCESS[x.access][0]}</span>
        {x.access === 'approve' && (x.approves.length ? x.approves : ['no sections ticked']).map((s) => <span key={s} className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs" data-testid={`sup-sec-${x.user_id}-${s}`}>{s}</span>)}
        {x.access === 'reports' && <span className="text-xs text-gray-500">{x.views.length ? x.views.join(', ') : 'all sections'}</span>}
      </div>
    ))}</div>
  </CardContent></Card>
);

export const AttendanceSettings = ({ sections, supervisors }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const fromApi = (d) => ({ ...d, shift_rows: Object.values(d.section_shifts || {}) });
  useEffect(() => {
    staffAttApi.settings().then((d) => setS(fromApi(d))).catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async () => {
    if (s.shift_rows.some((r) => !r.section)) { toast.error('Pick a section for every section shift (or remove the row)'); return; }
    const bad = s.locations.find((l) => !String(l.name || '').trim() || l.lat === '' || l.lng === '');
    if (bad) { toast.error(`${bad.name || 'A work location'}: enter a name and set the map pin`); return; }
    setBusy(true);
    try {
      const d = await staffAttApi.saveSettings({
        locations: s.locations, shift_female: s.shift_female, shift_male: s.shift_male, grace_min: Number(s.grace_min),
        weekly_off: s.weekly_off, section_shifts: Object.fromEntries(s.shift_rows.map((r, i) => [i, r])), liveness_required: !!s.liveness_required,
        absent_alert_pct: Number(s.absent_alert_pct), max_sessions: Number(s.max_sessions), day_close: s.day_close || '04:00',
        shift_day: s.shift_day, shift_night: s.shift_night, special_female: s.special_female, special_male: s.special_male,
        food_allowance: { food: Number(s.food_allowance.food), tea: Number(s.food_allowance.tea), after: s.food_allowance.after },
      });
      setS(fromApi(d));
      toast.success(d.rechecked_on_site ? `Attendance settings saved — ${d.rechecked_on_site} off-site punch(es) are now inside a work location` : 'Attendance settings saved');
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };
  if (!s) return <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-600" />;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="attendance-settings">
      <WorkLocations value={s.locations} onChange={(v) => setS((p) => ({ ...p, locations: v }))} />
      <ShiftSettings s={s} set={(patch) => setS({ ...s, ...patch })} sections={sections} />
      <SpecialDays s={s} set={(patch) => setS((p) => ({ ...p, ...patch }))} />
      <Card className="border-0 shadow-md" data-testid="headcount-settings"><CardContent className="p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-semibold">Headcount alert</h3>
          <p className="text-sm text-gray-500">A section card turns <b className="text-red-600">red</b> on the Headcount board when its absent staff are above this %.</p>
        </div>
        <div className="flex items-center gap-1">
          <Input type="number" min={0} max={100} value={s.absent_alert_pct} onChange={(e) => setS((p) => ({ ...p, absent_alert_pct: e.target.value }))} className="w-[100px] bg-white" data-testid="settings-absent-alert-input" />
          <span className="text-gray-500">%</span>
        </div>
      </CardContent></Card>
      <FoodAllowanceSettings value={s.food_allowance} onChange={(v) => setS((p) => ({ ...p, food_allowance: v }))} />
      <FixedPaySettings />
      <Approvers rows={supervisors} />
      <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="settings-save-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save Settings</Button>
    </div>
  );
};

export default AttendanceSettings;
